import {
  Box,
  Code,
  Heading,
  Text,
  Badge,
  VStack,
  HStack,
  Tooltip,
  IconButton,
  useColorModeValue,
  Icon,
  useBreakpointValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Textarea,
  Checkbox,
  RadioGroup,
  Radio,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stack as ChakraStack,
  Divider,
} from '@chakra-ui/react'
import { CheckIcon, TimeIcon, QuestionOutlineIcon } from '@chakra-ui/icons'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FiCheckSquare,
  FiCopy,
  FiExternalLink,
  FiEye,
  FiList,
  FiShield,
} from 'react-icons/fi'
import { useOrders, useOrderLifecycle } from '@/hooks/useAdminData'
import { DataTable } from '@/components/common/DataTable'
import { ColumnConfig, FilterConfig, ActionButton } from '@/components/common/DataTable/types'
import { ChainBadge } from '@/components/common/ChainBadge'
import { StatusBadge } from '@/components/common/StatusBadge'
import { TokenAmountDisplay } from '@/components/common/TokenAmountDisplay'
import { IdLookupPanel } from '@/components/common/IdLookupPanel'
import { useCopyableCodeStyles } from '@/components/common/useCopyableCodeStyles'
import type { OrderListItem } from '@/types'
import { getChainsByNetwork } from '@/constants/chains'
import { formatDateTime, getExplorerAddressUrl, truncateHash } from '@/utils/formatUtils'
import {
  extractAuditLogNote,
  extractNotesToPublic,
  normalizeNoteValue,
} from '@/utils/orderNotes'
import { ADMIN_ORDER_FILTER_STORAGE_KEY, withNetworkStorageKey } from '@/utils/storageKeys'
import { normalizeOrderStatusCode } from '@/utils/statusUtils'
import {
  readFunctionalStorage,
  removeFunctionalStorage,
  writeFunctionalStorage,
} from '@/utils/privacyPreferences'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { OrderLifecycleModal } from '@/components/orders/OrderLifecycleModal'
import { OrderAddressDoubleCheckModal } from '@/components/orders/OrderAddressDoubleCheckModal'
import { OrderSideEffectJobsModal } from '@/components/orders/OrderSideEffectJobsModal'
import { useToast } from '@/hooks/useToast'
import { orderService } from '@/services/admin/orderService'
import { auditService } from '@/services/admin/auditService'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { useOrderPauseAvailability } from '@/hooks/useOrderPauseAvailability'

const toIsoFromDateInput = (value?: string, endOfDay: boolean = false): string | undefined => {
  if (!value) {
    return undefined
  }

  const segments = value.split('-').map(part => Number(part))
  if (segments.length !== 3) {
    return undefined
  }

  const [year, month, day] = segments
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    year < 1970 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return undefined
  }

  const date = new Date(year, month - 1, day, 0, 0, 0, 0)
  if (endOfDay) {
    date.setHours(23, 59, 59, 999)
  }

  return date.toISOString()
}

const closeContractSkipReasonKeyMap: Record<string, string> = {
  queue_unavailable: 'orders.actions.closeContractSkipReasons.queueUnavailable',
  deployment_missing: 'orders.actions.closeContractSkipReasons.deploymentMissing',
  deployment_not_successful: 'orders.actions.closeContractSkipReasons.deploymentNotSuccessful',
  address_not_verified: 'orders.actions.closeContractSkipReasons.addressNotVerified',
  contract_address_missing: 'orders.actions.closeContractSkipReasons.contractAddressMissing',
  chain_missing: 'orders.actions.closeContractSkipReasons.chainMissing',
  chain_descriptor_unavailable:
    'orders.actions.closeContractSkipReasons.chainDescriptorUnavailable',
  refundable_balance_nonzero: 'orders.actions.closeContractSkipReasons.refundableBalanceNonzero',
  refundable_balance_check_failed:
    'orders.actions.closeContractSkipReasons.refundableBalanceCheckFailed',
  queue_enqueue_failed: 'orders.actions.closeContractSkipReasons.queueEnqueueFailed',
  already_paused: 'orders.actions.closeContractSkipReasons.alreadyPaused',
  active_registry_remaining: 'orders.actions.closeContractSkipReasons.activeRegistryRemaining',
  hold_commit_read_unavailable:
    'orders.actions.closeContractSkipReasons.holdCommitReadUnavailable',
  status_update_outcome_uncertain:
    'orders.actions.closeContractSkipReasons.statusUpdateOutcomeUncertain',
  solana_governance_enqueue_pending:
    'orders.actions.closeContractSkipReasons.governanceEnqueuePending',
  solana_governance_terminal:
    'orders.actions.closeContractSkipReasons.governanceTerminal',
  opposite_governance_request_active:
    'orders.actions.closeContractSkipReasons.oppositeGovernanceActive',
}

type PaymentConfirmationState =
  | 'confirmed'
  | 'pending'
  | 'partial'
  | 'expired'
  | 'refunded'
  | 'failed'
  | 'unknown'

const PaymentConfirmationBadge = ({ state }: { state?: PaymentConfirmationState }) => {
  const { t } = useTranslation()
  const showText = useBreakpointValue({ base: false, md: true })

  const meta: Record<PaymentConfirmationState, { color: string; icon: any; label: string }> = {
    confirmed: { color: 'green', icon: CheckIcon, label: t('orders.paymentConfirmation.confirmed') },
    pending: { color: 'orange', icon: TimeIcon, label: t('orders.paymentConfirmation.pending') },
    partial: { color: 'yellow', icon: QuestionOutlineIcon, label: t('orders.paymentConfirmation.partial') },
    expired: { color: 'gray', icon: QuestionOutlineIcon, label: t('orders.paymentConfirmation.expired') },
    refunded: { color: 'purple', icon: QuestionOutlineIcon, label: t('orders.paymentConfirmation.refunded') },
    failed: { color: 'red', icon: QuestionOutlineIcon, label: t('orders.paymentConfirmation.failed') },
    unknown: { color: 'gray', icon: QuestionOutlineIcon, label: t('orders.paymentConfirmation.unknown') },
  }

  const resolved = state ?? 'unknown'
  const { color, icon, label } = meta[resolved]
  const displayLabel =
    resolved === 'confirmed' ? label.replace(/\s+/gu, '\n') : label

  return (
    <Badge
      colorScheme={color}
      variant="solid"
      display="flex"
      alignItems="center"
      gap={showText ? 1 : 0}
      px={showText ? 2 : 1.5}
      title={label}
    >
      <Icon as={icon} boxSize={2.5} />
      {showText && (
        <Text
          fontSize="10px"
          lineHeight="1.1"
          textAlign="center"
          whiteSpace="pre-line"
        >
          {displayLabel}
        </Text>
      )}
    </Badge>
  )
}

export function OrdersPage() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const filterStorageKey = useMemo(
    () => withNetworkStorageKey(ADMIN_ORDER_FILTER_STORAGE_KEY, currentEnv),
    [currentEnv]
  )
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const loadStoredFilters = () => {
    if (typeof window === 'undefined') {
      return { persist: false, filters: {} as Record<string, any> }
    }

    try {
      const raw = readFunctionalStorage(filterStorageKey)
      if (!raw) {
        return { persist: false, filters: {} as Record<string, any> }
      }

      const parsed = JSON.parse(raw)
      if (parsed?.persist) {
        return {
          persist: true,
          filters: parsed.filters ?? {},
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[OrdersPage] Failed to load stored filters', error)
      }
      removeFunctionalStorage(filterStorageKey)
    }

    return { persist: false, filters: {} as Record<string, any> }
  }

  const storedConfig = useMemo(loadStoredFilters, [filterStorageKey])

  const [persistFilters, setPersistFilters] = useState<boolean>(storedConfig.persist)
  const [filters, setFilters] = useState<Record<string, any>>(storedConfig.filters)
  useEffect(() => {
    setPersistFilters(storedConfig.persist)
    setFilters(storedConfig.filters)
    setPage(1)
  }, [storedConfig])
  const [, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<OrderListItem | null>(null)
  const [sideEffectJobsOrder, setSideEffectJobsOrder] = useState<OrderListItem | null>(null)
  const [addressDoubleCheckOrder, setAddressDoubleCheckOrder] =
    useState<OrderListItem | null>(null)
  const [statusActionOrder, setStatusActionOrder] = useState<OrderListItem | null>(null)
  const [statusActionTab, setStatusActionTab] = useState<'onhold' | 'resume'>('onhold')
  const [statusActionTarget, setStatusActionTarget] = useState<
    'compliance_hold' | 'closed'
  >('compliance_hold')
  const [statusActionAuditLog, setStatusActionAuditLog] = useState('')
  const [statusActionNotesToPublic, setStatusActionNotesToPublic] = useState('')
  const [statusActionCloseContract, setStatusActionCloseContract] = useState(false)
  const statusActionPauseAvailability = useOrderPauseAvailability()
  const isStatusActionSolana =
    statusActionOrder?.chainCompatibility?.toLowerCase() === 'solana'
  const isSolanaOrderPauseUnsupported =
    isStatusActionSolana
    && statusActionOrder.solanaOrderPauseSupported !== true
  const isStatusActionPauseDisabled = isStatusActionSolana && (
    isSolanaOrderPauseUnsupported
    || statusActionPauseAvailability.isLoading
    || statusActionPauseAvailability.isUnavailable
    || statusActionPauseAvailability.isPaused
    || statusActionPauseAvailability.isClosed
    || statusActionPauseAvailability.isProgramPaused
    || statusActionPauseAvailability.hasPendingAction
  )
  const statusActionPauseDisabledReasonKey = isSolanaOrderPauseUnsupported
    ? 'orders.actions.solanaPauseUnsupported'
    : statusActionPauseAvailability.isLoading
      ? 'orders.actions.pauseStateLoading'
      : statusActionPauseAvailability.isUnavailable
        ? 'orders.actions.pauseStateUnavailable'
        : statusActionPauseAvailability.isPaused
          ? 'orders.actions.closeContractSkipReasons.alreadyPaused'
          : statusActionPauseAvailability.hasPendingAction
            ? 'orders.actions.pauseRequestPending'
            : statusActionPauseAvailability.isClosed
              ? 'orders.actions.pauseOrderClosed'
              : statusActionPauseAvailability.isProgramPaused
                ? 'orders.actions.pauseProgramPaused'
                : 'orders.actions.closeContractHint'
  const [isStatusSubmitting, setIsStatusSubmitting] = useState(false)
  const {
    isOpen: isLifecycleModalOpen,
    onOpen: openLifecycleModal,
    onClose: closeLifecycleModal,
  } = useDisclosure()
  const {
    isOpen: isSideEffectJobsModalOpen,
    onOpen: openSideEffectJobsModal,
    onClose: closeSideEffectJobsModal,
  } = useDisclosure()
  const {
    isOpen: isAddressDoubleCheckModalOpen,
    onOpen: openAddressDoubleCheckModal,
    onClose: closeAddressDoubleCheckModal,
  } = useDisclosure()
  const {
    isOpen: isStatusModalOpen,
    onOpen: openStatusModal,
    onClose: closeStatusModal,
  } = useDisclosure()

  const textColor = useColorModeValue('gray.600', 'gray.300')
  const copyableCodeStyles = useCopyableCodeStyles()
  const {
    warning: showWarningToast,
    success: showSuccessToast,
    error: showErrorToast,
    info: showInfoToast,
  } = useToast()
  const statusActionOrderIdRef = useRef<string | null>(null)
  const previousEnvironmentRef = useRef(currentEnv)

  useEffect(() => {
    if (isStatusActionPauseDisabled) {
      setStatusActionCloseContract(false)
    }
  }, [isStatusActionPauseDisabled])

  useEffect(() => {
    if (previousEnvironmentRef.current === currentEnv) {
      return
    }
    previousEnvironmentRef.current = currentEnv
    setSelectedOrder(null)
    setSideEffectJobsOrder(null)
    setAddressDoubleCheckOrder(null)
    setStatusActionOrder(null)
    setStatusActionCloseContract(false)
    statusActionPauseAvailability.reset()
    closeLifecycleModal()
    closeSideEffectJobsModal()
    closeAddressDoubleCheckModal()
    closeStatusModal()
  }, [
    closeAddressDoubleCheckModal,
    closeLifecycleModal,
    closeSideEffectJobsModal,
    closeStatusModal,
    currentEnv,
    statusActionPauseAvailability.reset,
  ])

  const setNotesToPublicIfEmpty = (value?: string | null) => {
    const normalized = normalizeNoteValue(value)
    if (!normalized) {
      return
    }
    setStatusActionNotesToPublic((current) => (current.trim() ? current : normalized))
  }

  const blurActiveElement = () => {
    if (typeof document === 'undefined') {
      return
    }
    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement) {
      activeElement.blur()
    }
  }

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showSuccessToast({
        title: t('common.success'),
        description: `${label} copied`,
      })
    } catch {
      showErrorToast({
        title: t('common.error'),
        description: `Failed to copy ${label}`,
      })
    }
  }

  const lifecycleIncludes = useMemo<string[]>(
    () => [
      'order',
      'preparation',
      'rentRecovery',
      'deployments',
      'sweepExtend',
      'payments',
      'settlements',
      'refunds',
      'contractPauseResumeCloseEvents',
      'timeline',
      'onchainCosts',
    ],
    []
  )

  const createdAtFromIso = useMemo(
    () => toIsoFromDateInput(filters.createdAt_from, false),
    [filters.createdAt_from]
  )
  const createdAtToIso = useMemo(
    () => toIsoFromDateInput(filters.createdAt_to, true),
    [filters.createdAt_to]
  )

  const orderQueryParams = useMemo(
    () => ({
      page,
      limit: pageSize,
      search: filters.search || '',
      status: filters.status || '',
      merchant_id: filters.merchantId || '',
      chain_id: filters.chainId || '',
      createdAtFrom: createdAtFromIso,
      createdAtTo: createdAtToIso,
    }),
    [
      page,
      pageSize,
      filters.search,
      filters.status,
      filters.merchantId,
      filters.chainId,
      createdAtFromIso,
      createdAtToIso,
    ]
  )

  const { data, isLoading, error, refetch, isFetching } = useOrders(orderQueryParams)

  const lifecycleQuery = useOrderLifecycle(selectedOrder?.orderId ?? null, lifecycleIncludes, {
    enabled: isLifecycleModalOpen,
  })
  const isLifecycleLoading = lifecycleQuery.isLoading || lifecycleQuery.isFetching

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    if (!persistFilters) {
      removeFunctionalStorage(filterStorageKey)
      return
    }

    const payload = {
      persist: true,
      filters,
    }

    try {
      writeFunctionalStorage(filterStorageKey, JSON.stringify(payload))
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[OrdersPage] Failed to persist filters', error)
      }
    }
  }, [persistFilters, filters])

  const isFinalStatus = (statusCode: string | undefined) => {
    const normalized = normalizeOrderStatusCode(statusCode)
    return [
      'settled',
      'full_refunded',
      'refunded',
      'expired',
      'closed',
    ].includes(normalized)
  }

  const getStatusText = (statusCode: string) => {
    if (!statusCode) return t('common.pending')
    const normalized = normalizeOrderStatusCode(statusCode)
    const statusMap: Record<string, string> = {
      awaiting_payment: 'orders.status.awaitingPayment',
      underpaid: 'orders.status.underpaid',
      fullpaid: 'orders.status.fullPaid',
      overpaid: 'orders.status.overpaid',
      expired: 'orders.status.expired',
      settled: 'orders.status.settled',
      refunded: 'orders.status.refunded',
      compliance_hold: 'orders.status.complianceHold',
      closed: 'orders.status.closed',
    }
    const key = statusMap[normalized as keyof typeof statusMap]
    if (key) {
      return t(key)
    }
    return normalized.replace(/_/g, ' ').toUpperCase()
  }

  const getPaymentConfirmationState = (row: OrderListItem): PaymentConfirmationState => {
    if (typeof row.paymentConfirmationState === 'string') {
      const normalized = row.paymentConfirmationState.toLowerCase() as PaymentConfirmationState
      if (['confirmed', 'pending', 'partial', 'expired', 'refunded', 'failed', 'unknown'].includes(normalized)) {
        return normalized
      }
    }

    const normalizedStatus = normalizeOrderStatusCode(row.status)

    if (normalizedStatus === 'full_refunded' || normalizedStatus === 'refunded') {
      return 'refunded'
    }
    if (normalizedStatus === 'expired') {
      return 'expired'
    }
    if (['underpaid', 'overpaid', 'partial'].includes(normalizedStatus)) {
      return 'partial'
    }
    if (normalizedStatus === 'failed') {
      return 'failed'
    }
    const loweredState = row.paymentConfirmationState?.toLowerCase() as PaymentConfirmationState | undefined
    if (loweredState === 'pending') {
      return 'pending'
    }
    return 'unknown'
  }

  // Define table columns
  const columns: ColumnConfig<OrderListItem>[] = [
    {
      key: 'orderId',
      title: t('orders.table.orderId'),
      width: '16%',
      sortable: true,
      render: (_value, row) => (
        <Tooltip label={row.orderId ? t('common.copy') : ''}>
          <Box minH="24px" display="flex" alignItems="center" justifyContent="flex-start">
            <Code
              as="div"
              bg="transparent"
              fontFamily="mono"
              fontSize="xs"
              lineHeight="short"
              textShadow="none"
              wordBreak="break-all"
              cursor={row.orderId ? 'pointer' : 'default'}
              _hover={row.orderId ? copyableCodeStyles.hover : undefined}
              _active={row.orderId ? copyableCodeStyles.active : undefined}
              onClick={() => row.orderId && void copyToClipboard(row.orderId, 'Order ID')}
            >
              {row.orderId}
            </Code>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'merchantBusinessId',
      title: t('orders.table.merchantId', 'Merchant ID'),
      width: '14%',
      sortable: true,
      render: (_value, row) => {
        const displayValue = row.merchantBusinessId || row.merchantId || t('common.pending')
        const copyValue = row.merchantBusinessId || row.merchantId
        return (
          <Tooltip label={copyValue ? t('common.copy') : ''}>
            <Box minH="24px" display="flex" alignItems="center" justifyContent="flex-start" maxH="3.9em" overflowY="auto" pr={1}>
              <Code
                as="div"
                bg="transparent"
                fontWeight="medium"
                fontSize="xs"
                lineHeight="1.3"
                whiteSpace="normal"
                wordBreak="break-all"
                cursor={copyValue ? 'pointer' : 'default'}
                _hover={copyValue ? copyableCodeStyles.hover : undefined}
                _active={copyValue ? copyableCodeStyles.active : undefined}
                onClick={() => copyValue && void copyToClipboard(copyValue, 'Merchant ID')}
              >
                {displayValue}
              </Code>
            </Box>
          </Tooltip>
        )
      },
    },
    {
      key: 'amountUsd',
      title: t('orders.table.amount'),
      width: '12%',
      sortable: true,
      render: (_value, row) => {
        const numeric = Number(row.amountUsd)
        if (!Number.isFinite(numeric)) {
          return <Text>{t('common.pending')}</Text>
        }
        return <TokenAmountDisplay amount={numeric} symbol={row.tokenSymbol} />
      },
    },
    {
      key: 'paidAmountUsd',
      title: t('orders.table.paidAmount', 'Paid Amount'),
      width: '10%',
      sortable: false,
      render: (_value, row) => {
        const numeric = Number(row.paidAmountUsd)
        return (
          <TokenAmountDisplay
            amount={Number.isFinite(numeric) ? numeric : row.paidAmountUsd}
            symbol={row.tokenSymbol}
          />
        )
      },
    },
    {
      key: 'settledAmountUsd',
      title: t('orders.table.settledAmount', 'Settled Amount'),
      width: '10%',
      sortable: false,
      render: (_value, row) => {
        const numeric = Number(row.settledAmountUsd)
        return (
          <TokenAmountDisplay
            amount={Number.isFinite(numeric) ? numeric : row.settledAmountUsd}
            symbol={row.tokenSymbol}
          />
        )
      },
    },
    {
      key: 'paymentAddress',
      title: t('orders.table.paymentAddress', 'Payment Address'),
      width: '16%',
      sortable: false,
      render: (value, row) => {
        const address = value ?? row.paymentAddress
        const explorerUrl = address ? getExplorerAddressUrl(row.chainId || '', address) : undefined
        return address && address.trim().length > 0 ? (
          <HStack spacing={1} maxW="100%">
            <Code fontSize="xs" flex="1" title={address}>
              {truncateHash(address)}
            </Code>
            <Tooltip label="Copy Address">
              <IconButton
                aria-label="Copy Address"
                icon={<FiCopy />}
                size="xs"
                variant="ghost"
                onClick={() => void copyToClipboard(address, 'Address')}
              />
            </Tooltip>
            {explorerUrl && (
              <Tooltip label="View on Explorer">
                <IconButton
                  aria-label="View on Explorer"
                  icon={<FiExternalLink />}
                  size="xs"
                  variant="ghost"
                  onClick={() => window.open(explorerUrl, '_blank', 'noopener,noreferrer')}
                />
              </Tooltip>
            )}
          </HStack>
        ) : (
          <Text>{t('common.na')}</Text>
        )
      },
    },
    {
      key: 'chainLabel',
      title: t('orders.table.chain'),
      width: '12%',
      sortable: true,
      render: (_value, row) => (
        <ChainBadge
          chainId={row.chainId}
          chainCode={row.chainCode}
          chainName={row.chainName ?? row.chainLabel}
          chainNumericId={row.chainNumericId}
          isTestnet={row.isTestnet}
        />
      ),
    },
    {
      key: 'status',
      title: t('orders.table.status'),
      width: '12%',
      sortable: true,
      render: (_value, row) => (
        <StatusBadge statusType="order" status={row.status} />
      ),
    },
    {
      key: 'paymentConfirmationState',
      title: t('orders.table.paymentConfirmation'),
      width: '15%',
      sortable: true,
      render: (_value, row) => <PaymentConfirmationBadge state={getPaymentConfirmationState(row)} />,
    },
    {
      key: 'hasRefunded',
      title: t('orders.table.hasRefunded', 'Refunded'),
      width: '8%',
      sortable: false,
      render: (_value, row) => (
        <Badge colorScheme={row.hasRefunded ? 'purple' : 'gray'}>
          {row.hasRefunded ? t('common.yes') : t('common.no')}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      title: t('orders.table.created'),
      width: '11%',
      sortable: true,
      render: (_value, row) => (
        <Text fontSize="sm">
          {row?.createdAt
            ? formatDateTime(row.createdAt, {
                locale: typeof window !== 'undefined' ? navigator.language : 'en-US',
              })
            : t('common.na')}
        </Text>
      ),
    },
  ]

  // Define filter configuration
  const filterConfigs: FilterConfig[] = [
    {
      key: 'search',
      type: 'text',
      label: 'Search',
      placeholder: t('orders.searchPlaceholder'),
      width: '300px',
      maxLength: 128,
    },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      placeholder: t('orders.filterByStatus'),
      width: '200px',
      options: [
        { value: 'ost_awaiting_payment', label: t('orders.status.awaitingPayment') },
        { value: 'ost_underpaid', label: t('orders.status.underpaid') },
        { value: 'ost_fullpaid', label: t('orders.status.fullPaid') },
        { value: 'ost_overpaid', label: t('orders.status.overpaid') },
        { value: 'ost_expired', label: t('orders.status.expired') },
        { value: 'ost_settled', label: t('orders.status.settled') },
        { value: 'ost_full_refunded', label: t('orders.status.refunded') },
        { value: 'ost_compliance_hold', label: t('orders.status.complianceHold') },
        { value: 'ost_closed', label: t('orders.status.closed') },
      ],
    },
    {
      key: 'chainId',
      type: 'select',
      label: 'Chain',
      placeholder: t('orders.filterByChain'),
      width: '260px',
      options: getChainsByNetwork(currentEnv).map(chain => ({
        value: String(chain.numericChainId),
        label: chain.shortName,
      })),
    },
    {
      key: 'createdAt',
      type: 'dateRange',
      label: t('orders.filterByCreatedAt'),
      width: '260px',
    },
    {
      key: 'merchantId',
      type: 'text',
      label: 'Merchant',
      placeholder: t('orders.filterByMerchant'),
      width: '300px',
    },
  ]

  // Define row actions
  const handleViewLifecycle = (row: OrderListItem) => {
    blurActiveElement()
    setSelectedOrder(row)
    openLifecycleModal()
  }

  const handleCloseLifecycleModal = () => {
    setSelectedOrder(null)
    closeLifecycleModal()
  }

  const handleViewSideEffectJobs = (row: OrderListItem) => {
    blurActiveElement()
    setSideEffectJobsOrder(row)
    openSideEffectJobsModal()
  }

  const handleCloseSideEffectJobsModal = () => {
    setSideEffectJobsOrder(null)
    closeSideEffectJobsModal()
  }

  const handleViewAddressDoubleCheck = (row: OrderListItem) => {
    blurActiveElement()
    setAddressDoubleCheckOrder(row)
    openAddressDoubleCheckModal()
  }

  const handleCloseAddressDoubleCheckModal = () => {
    setAddressDoubleCheckOrder(null)
    closeAddressDoubleCheckModal()
  }

  const handleRefreshLifecycle = () => {
    lifecycleQuery.refetch()
  }

  const loadStatusActionAuditLog = async (row: OrderListItem) => {
    if (!row?.orderId) {
      return
    }

    const targetOrderId = row.orderId
    try {
      const response = await auditService.getAuditLogs({
        page: 1,
        limit: 10,
        resource_type: 'order',
        resource_id: row.orderId,
      })

      if (!response.success) {
        return
      }

      const logs = Array.isArray(response.data) ? response.data : []
      const targetLog = logs.find(log => extractAuditLogNote(log.details))
      if (targetLog) {
        const auditLog = extractAuditLogNote(targetLog.details)
        if (auditLog && statusActionOrderIdRef.current === targetOrderId) {
          setStatusActionAuditLog((current) => (current.trim() ? current : auditLog))
        }
      }

      const existingNotes = normalizeNoteValue(row.notesToPublic ?? null)
      if (!existingNotes) {
        const noteLog = logs.find(log => extractNotesToPublic(log.details))
        if (noteLog) {
          const notes = normalizeNoteValue(extractNotesToPublic(noteLog.details))
          if (notes && statusActionOrderIdRef.current === targetOrderId) {
            setNotesToPublicIfEmpty(notes)
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[OrdersPage] Failed to load audit log', error)
      }
    }
  }

  const loadStatusActionOrderDetails = async (row: OrderListItem) => {
    if (!row?.orderId) {
      return
    }

    try {
      const response = await orderService.getAllOrders(
        {
          page: 1,
          pageSize: 1,
          search: row.orderId,
        },
        currentEnv
      )

      const matches = Array.isArray(response.data) ? response.data : []
      const matched = matches.find(item => item.orderId === row.orderId) ?? matches[0]
      if (!matched || statusActionOrderIdRef.current !== row.orderId) {
        return
      }

      setStatusActionOrder(matched)

      const normalizedNotes = normalizeNoteValue(matched.notesToPublic ?? null)
      if (normalizedNotes) {
        setNotesToPublicIfEmpty(normalizedNotes)
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[OrdersPage] Failed to refresh order details', error)
      }
    }
  }

  const handleOpenStatusModal = (row: OrderListItem) => {
    blurActiveElement()
    const normalizedStatus = normalizeOrderStatusCode(row.status)
    const isComplianceHold = normalizedStatus === 'compliance_hold'
    setStatusActionOrder(row)
    setStatusActionTab(isComplianceHold ? 'resume' : 'onhold')
    setStatusActionTarget('compliance_hold')
    setStatusActionAuditLog('')
    setStatusActionNotesToPublic('')
    setStatusActionCloseContract(false)
    statusActionPauseAvailability.reset()
    statusActionOrderIdRef.current = row.orderId
    openStatusModal()
    setNotesToPublicIfEmpty(row.notesToPublic ?? null)
    void loadStatusActionAuditLog(row)
    void loadStatusActionOrderDetails(row)
    if (
      row.chainCompatibility?.toLowerCase() === 'solana'
      && row.solanaOrderPauseSupported === true
    ) {
      void statusActionPauseAvailability.load(row.orderId, currentEnv)
    }
  }

  const handleCloseStatusModal = () => {
    setStatusActionOrder(null)
    setStatusActionAuditLog('')
    setStatusActionNotesToPublic('')
    setStatusActionCloseContract(false)
    statusActionPauseAvailability.reset()
    setStatusActionTarget('compliance_hold')
    setStatusActionTab('onhold')
    statusActionOrderIdRef.current = null
    closeStatusModal()
  }

  const handleConfirmStatusChange = async () => {
    if (!statusActionOrder) {
      return
    }

    const auditLog = statusActionAuditLog.trim()
    if (!auditLog) {
      showErrorToast({
        titleKey: 'orders.actions.auditLogRequired',
        descriptionKey: 'orders.actions.auditLogRequiredDescription',
        isClosable: true,
      })
      return
    }

    setIsStatusSubmitting(true)
    try {
      const response = await orderService.onholdOrCloseOrder(
        statusActionOrder.orderId,
        {
          status: statusActionTarget,
          auditLog,
          notesToPublic: statusActionNotesToPublic.trim() || undefined,
          closeContract: statusActionCloseContract && !isStatusActionPauseDisabled,
        },
        currentEnv
      )

      if (!response.success) {
        const message = response.error ?? response.message ?? t('orders.actions.statusUpdateFailed')
        throw new Error(message)
      }

      if (response.data?.closeContractSkipReason && statusActionCloseContract) {
        const skipReasonKey =
          closeContractSkipReasonKeyMap[response.data.closeContractSkipReason] ??
          'orders.actions.closeContractSkippedDescription'
        showWarningToast({
          titleKey: 'orders.actions.closeContractSkipped',
          descriptionKey: skipReasonKey,
          isClosable: true,
        })
      } else if (response.data?.closeContractQueued && statusActionCloseContract) {
        showInfoToast({
          titleKey: 'orders.actions.pauseQueued',
          descriptionKey: 'orders.actions.pauseQueuedDescription',
          isClosable: true,
        })
      }

      showSuccessToast({
        titleKey: 'orders.actions.statusUpdated',
        descriptionKey: 'orders.actions.statusUpdatedDescription',
        isClosable: true,
      })
      handleCloseStatusModal()
      refetch()
    } catch (error) {
      showErrorToast({
        titleKey: 'orders.actions.statusUpdateFailed',
        description: error instanceof Error ? error.message : String(error),
        isClosable: true,
      })
    } finally {
      setIsStatusSubmitting(false)
    }
  }

  const handleConfirmResumeFromHold = async () => {
    if (!statusActionOrder) {
      return
    }

    const auditLog = statusActionAuditLog.trim()
    if (!auditLog) {
      showErrorToast({
        titleKey: 'orders.actions.auditLogRequired',
        descriptionKey: 'orders.actions.auditLogRequiredDescription',
        isClosable: true,
      })
      return
    }

    setIsStatusSubmitting(true)
    try {
      const response = await orderService.resumeFromHoldOrder(
        statusActionOrder.orderId,
        {
          auditLog,
          notesToPublic: statusActionNotesToPublic.trim() || undefined,
        },
        currentEnv
      )

      if (!response.success) {
        const message = response.error ?? response.message ?? t('orders.actions.statusUpdateFailed')
        throw new Error(message)
      }

      if (response.data?.releasePending) {
        showInfoToast({
          titleKey: 'orders.actions.resumePending',
          descriptionKey: 'orders.actions.resumePendingDescription',
          isClosable: true,
        })
      } else {
        showSuccessToast({
          titleKey: 'orders.actions.statusUpdated',
          descriptionKey: 'orders.actions.statusUpdatedDescription',
          isClosable: true,
        })
      }
      handleCloseStatusModal()
      refetch()
    } catch (error) {
      showErrorToast({
        titleKey: 'orders.actions.statusUpdateFailed',
        description: error instanceof Error ? error.message : String(error),
        isClosable: true,
      })
    } finally {
      setIsStatusSubmitting(false)
    }
  }

  const statusModalTabIndex = statusActionTab === 'resume' ? 1 : 0
  const handleStatusModalSubmit =
    statusActionTab === 'resume' ? handleConfirmResumeFromHold : handleConfirmStatusChange

  const getRowActions = (row: OrderListItem): ActionButton[] => {
    const actions: ActionButton[] = [
      {
        label: '',
        icon: <FiEye />,
        tooltip: t('orders.actions.viewLifecycle'),
        onClick: () => handleViewLifecycle(row),
        size: 'xs',
        variant: 'ghost',
      },
      {
        label: t('orders.actions.sideEffectJobs'),
        icon: <FiList />,
        tooltip: t('orders.actions.sideEffectJobs'),
        onClick: () => handleViewSideEffectJobs(row),
        size: 'xs',
        variant: 'ghost',
      },
      {
        label: t('orders.actions.addressDoubleCheck'),
        icon: <FiCheckSquare />,
        tooltip: t('orders.actions.addressDoubleCheck'),
        onClick: () => handleViewAddressDoubleCheck(row),
        size: 'xs',
        variant: 'ghost',
      },
    ]

    if (!isFinalStatus(row.status)) {
      actions.push({
        label: t('orders.actions.onholdOrClose'),
        icon: <FiShield />,
        tooltip: t('orders.actions.onholdOrClose'),
        onClick: () => handleOpenStatusModal(row),
        size: 'xs',
        variant: 'ghost',
      })
    }

    return actions
  }

  // Handle pagination
  const paginationConfig = data?.pagination ? {
    page,
    pageSize,
    total: data.pagination.total,
    pageSizeOptions: [10, 20, 50, 100],
  } : undefined

  const handleExport = async () => {
    try {
      const { blob, filename } = await orderService.exportCsv({
        search: filters.search || '',
        status: filters.status || '',
        merchant_id: filters.merchantId || '',
        chain_id: filters.chainId || '',
        createdAtFrom: createdAtFromIso,
        createdAtTo: createdAtToIso,
      }, currentEnv)
      triggerBrowserDownload(blob, filename)
      showSuccessToast({
        titleKey: 'common.export.success',
        descriptionKey: 'common.export.downloaded',
        isClosable: true,
      })
    } catch (error) {
      showErrorToast({
        titleKey: 'common.export.failed',
        description:
          error instanceof Error ? error.message : t('common.operationFailed'),
        isClosable: true,
      })
    }
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <IdLookupPanel entity="order" />
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>
            {t('orders.title')}
          </Heading>
          <Text color={textColor}>
            {t('orders.description')}
          </Text>
        </Box>

        {/* DataTable */}
        <DataTable
          data={data?.data || []}
          loading={isLoading}
          error={error}
          columns={columns}
          filters={filterConfigs}
          initialFilters={filters}
          onFiltersChange={setFilters}
          showFilterBar={true}
          filterLayout="split"
          persistOption={{
            label: t('orders.rememberFilters'),
            checked: persistFilters,
            onChange: setPersistFilters,
            colorScheme: 'blue',
          }}
          sortable={true}
          onSortChange={setSort}
          pagination={paginationConfig}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          actions={getRowActions}
          actionsColumnWidth="5%"
          size="sm"
          emptyMessage={t('orders.noOrdersFound')}
          showRefresh={true}
          onRefresh={refetch}
          refreshing={isFetching}
          showExport={true}
          onExport={handleExport}
          mobileBreakpoint="md"
        />
      </VStack>

      <OrderLifecycleModal
        isOpen={isLifecycleModalOpen}
        onClose={handleCloseLifecycleModal}
        order={selectedOrder}
        lifecycle={lifecycleQuery.data}
        isLoading={isLifecycleLoading}
        onRefresh={handleRefreshLifecycle}
        mode={currentEnv}
      />

      <OrderSideEffectJobsModal
        key={`${currentEnv}:${sideEffectJobsOrder?.orderId ?? 'closed'}`}
        isOpen={isSideEffectJobsModalOpen}
        onClose={handleCloseSideEffectJobsModal}
        order={sideEffectJobsOrder}
      />

      <OrderAddressDoubleCheckModal
        key={`${currentEnv}:${addressDoubleCheckOrder?.orderId ?? 'closed'}`}
        isOpen={isAddressDoubleCheckModalOpen}
        onClose={handleCloseAddressDoubleCheckModal}
        order={addressDoubleCheckOrder}
      />

      <Modal
        isOpen={isStatusModalOpen}
        onClose={handleCloseStatusModal}
        isCentered
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('orders.actions.onholdOrCloseTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ChakraStack spacing={3}>
              <FormControl>
                <ChakraStack direction="row" align="center" justify="space-between">
                  <FormLabel fontSize="sm" mb={0}>
                    {t('orders.actions.currentStatus')}
                  </FormLabel>
                  <Text fontSize="sm" color={textColor}>
                    {statusActionOrder
                      ? getStatusText(statusActionOrder.status)
                      : t('common.pending')}
                  </Text>
                </ChakraStack>
              </FormControl>

              <Tabs
                index={statusModalTabIndex}
                onChange={(index) => {
                  const next = index === 1 ? 'resume' : 'onhold'
                  setStatusActionTab(next)
                  if (next === 'resume') {
                    setStatusActionCloseContract(false)
                  }
                }}
                size="sm"
                variant="enclosed"
              >
                <TabList>
                  <Tab>{t('orders.actions.onholdOrCloseTab')}</Tab>
                  <Tab>{t('orders.actions.resumeFromHoldTab')}</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0} pt={3}>
                    <ChakraStack spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('orders.actions.targetStatus')}</FormLabel>
                        <RadioGroup
                          value={statusActionTarget}
                          onChange={(value) => {
                            const next = value as 'compliance_hold' | 'closed'
                            setStatusActionTarget(next)
                          }}
                        >
                          <ChakraStack direction="row" spacing={6}>
                            <Radio value="compliance_hold">
                              {t('orders.actions.targetComplianceHold')}
                            </Radio>
                            <Radio value="closed">{t('orders.actions.targetClosed')}</Radio>
                          </ChakraStack>
                        </RadioGroup>
                      </FormControl>

                      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">{t('orders.actions.auditLog')}</FormLabel>
                          <Textarea
                            value={statusActionAuditLog}
                            onChange={(event) => setStatusActionAuditLog(event.target.value)}
                            placeholder={t('orders.actions.auditLogPlaceholder')}
                            rows={3}
                          />
                          <FormHelperText>{t('orders.actions.auditLogHelper')}</FormHelperText>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">{t('orders.actions.notesToPublic')}</FormLabel>
                          <Textarea
                            value={statusActionNotesToPublic}
                            onChange={(event) => setStatusActionNotesToPublic(event.target.value)}
                            placeholder={t('orders.actions.notesToPublicPlaceholder')}
                            rows={3}
                          />
                        </FormControl>
                      </SimpleGrid>

                      <Divider />

                      <FormControl>
                        <Checkbox
                          isChecked={statusActionCloseContract}
                          onChange={(event) => setStatusActionCloseContract(event.target.checked)}
                          isDisabled={isStatusActionPauseDisabled}
                        >
                          {t('orders.actions.closeContract')}
                        </Checkbox>
                        <FormHelperText>
                          {t(statusActionPauseDisabledReasonKey)}
                        </FormHelperText>
                      </FormControl>
                    </ChakraStack>
                  </TabPanel>
                  <TabPanel px={0} pt={3}>
                    <ChakraStack spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">
                          {t('orders.actions.resumeTargetStatus')}
                        </FormLabel>
                        <Text fontSize="sm">
                          {t('orders.actions.resumeDerivedStatusHint')}
                        </Text>
                      </FormControl>

                      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">{t('orders.actions.auditLog')}</FormLabel>
                          <Textarea
                            value={statusActionAuditLog}
                            onChange={(event) => setStatusActionAuditLog(event.target.value)}
                            placeholder={t('orders.actions.auditLogPlaceholder')}
                            rows={3}
                          />
                          <FormHelperText>{t('orders.actions.auditLogHelper')}</FormHelperText>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">{t('orders.actions.notesToPublic')}</FormLabel>
                          <Textarea
                            value={statusActionNotesToPublic}
                            onChange={(event) => setStatusActionNotesToPublic(event.target.value)}
                            placeholder={t('orders.actions.notesToPublicPlaceholder')}
                            rows={3}
                          />
                        </FormControl>
                      </SimpleGrid>

                      <Divider />

                      <FormControl>
                        <FormHelperText>
                          {t('orders.actions.resumeAutomaticChainHint')}
                        </FormHelperText>
                      </FormControl>
                    </ChakraStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </ChakraStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseStatusModal}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="red"
              onClick={handleStatusModalSubmit}
              isLoading={isStatusSubmitting}
            >
              {t('orders.actions.confirmStatusChange')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


    </Box>
  )
}
